import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { formatInrPaise } from "../../domain/tiers";
import { istTime } from "../../domain/istTime";
import {
  CHARGE_BASIS_LABEL,
  USAGE_PAID_BY_LABEL,
  chargeText,
  CYCLE_LABEL,
  GST_RATES,
  LICENCE_TYPES,
  bandwidthText,
  createAgreement,
  endAgreement,
  formatBandwidth,
  fromInclusive,
  gstSplit,
  listAgreements,
  listBills,
  paiseToRupees,
  recordBill,
  rupeesToPaise,
  setBillPayment,
  toMbps,
  updateAgreement,
  upstreamRefusal,
  voidBill,
  type ChargeBasis,
  type UsagePaidBy,
  type BandwidthUnit,
  type BillingCycle,
  type UpstreamAgreement,
  type UpstreamAgreementInput,
  type UpstreamBill,
} from "../../api/upstream";

const inr = (paise: number) => formatInrPaise(paise / 100);
const holdsAny = (roles: string[], allowed: readonly string[]) => roles.some((r) => allowed.includes(r.toLowerCase()));
const today = () => new Date().toISOString().slice(0, 10);

/** "₹10,000 + GST 18% a quarter" or "₹11,800 incl. GST 18% a quarter". */
const priceText = (a: UpstreamAgreement) =>
  `${inr(a.commitmentPaise)} ${a.priceIncludesGst ? "incl." : "+"} GST ${a.gstRateBp / 100}% ${CYCLE_LABEL[a.billingCycle].per}`;

/**
 * Billing › Upstream supplier (owner OB-9/14/15, 2026-10-28; radaccess v1.193; per-cycle
 * charge, half-yearly, GST incl./extra and Mbps/Gbps from radaccess v1.194 + SQL 267).
 *
 * What this provider buys from its outside bandwidth supplier -- Mbps,
 * commitment, term -- and the supplier's tax invoices, so margins and input
 * GST have a record. Owner, Admin and Billing record; superadmin reads.
 * Nothing is deleted: an agreement is ended and a bill is voided, each with a
 * reason, and radaudit keeps every change.
 */
export function UpstreamScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const canWrite = holdsAny(identity.roles, ["operator", "manager", "billing"]);
  const qc = useQueryClient();
  const key = ["upstream-agreements", ns];
  const agreements = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listAgreements(identity, ns, signal),
    retry: false,
  });
  const [editing, setEditing] = useState<UpstreamAgreement | "new" | null>(null);
  const [ending, setEnding] = useState<UpstreamAgreement | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Upstream supplier
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The bandwidth you buy from an outside supplier, and the supplier's tax invoices. Nothing here is deleted: end an agreement or
          void a bill, with a reason.
        </Typography>
      </Box>
      {canWrite && (
        <Box>
          <Button variant="contained" onClick={() => setEditing("new")}>
            Add agreement
          </Button>
        </Box>
      )}
      {agreements.isLoading && <Typography color="text.secondary">Loading…</Typography>}
      {agreements.error && <Alert severity="error">{upstreamRefusal(agreements.error)}</Alert>}
      {agreements.data && agreements.data.length === 0 && <Typography color="text.secondary">No upstream agreement recorded yet.</Typography>}
      {(agreements.data ?? []).map((a) => (
        <Paper key={a.id} variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Box>
              <Typography variant="subtitle1">
                {a.supplierLegalName}{" "}
                <Chip size="small" color={a.status === "ended" ? "default" : "success"} label={a.status === "ended" ? "Ended" : "Active"} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatBandwidth(a.committedMbps, a.bandwidthUnit)} · {chargeText(a.chargeBasis, a.usagePaidBy)}
                {a.commitmentPaise > 0 && ` · ${priceText(a)}`} · billed {CYCLE_LABEL[a.billingCycle].name.toLowerCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {a.supplierGstin ? `GSTIN ${a.supplierGstin}` : "No GSTIN recorded"}
                {a.supplierLicenseType && ` · ${a.supplierLicenseType}${a.supplierLicenseNumber ? ` ${a.supplierLicenseNumber}` : ""}`}
                {a.agreementRef && ` · ref ${a.agreementRef}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From {a.termStart}
                {a.termEnd ? ` to ${a.termEnd}` : ", open-ended"}
                {a.status === "ended" && a.endedAt && ` · ended ${istTime(a.endedAt)}: ${a.endReason ?? ""}`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setOpen(open === a.id ? null : a.id)}>
                {open === a.id ? "Hide bills" : "Bills"}
              </Button>
              {canWrite && a.status !== "ended" && (
                <>
                  <Button size="small" onClick={() => setEditing(a)}>
                    Edit
                  </Button>
                  <Button size="small" color="warning" onClick={() => setEnding(a)}>
                    End
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
          {open === a.id && <Bills ns={ns} agreement={a} canWrite={canWrite} />}
        </Paper>
      ))}
      {editing && (
        <AgreementDialog
          ns={ns}
          agreement={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
      {ending && (
        <ReasonDialog
          title={`End the agreement with ${ending.supplierLegalName}`}
          help="It stays on record as ended; bills that arrive later can still be recorded against it."
          action="End agreement"
          run={(reason) => endAgreement(identity, ns, ending.id, reason)}
          onClose={() => setEnding(null)}
          onDone={() => {
            refresh();
            setEnding(null);
          }}
        />
      )}
    </Box>
  );
}

function AgreementDialog({ ns, agreement, onClose, onDone }: { ns: string; agreement: UpstreamAgreement | null; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const a = agreement;
  const [f, setF] = useState({
    supplierLegalName: a?.supplierLegalName ?? "",
    supplierGstin: a?.supplierGstin ?? "",
    supplierLicenseType: a?.supplierLicenseType ?? "",
    supplierLicenseNumber: a?.supplierLicenseNumber ?? "",
    agreementRef: a?.agreementRef ?? "",
    chargeBasis: (a?.chargeBasis ?? "fixed") as ChargeBasis,
    usagePaidBy: (a?.usagePaidBy ?? "") as UsagePaidBy | "",
    bandwidth: a ? bandwidthText(a.committedMbps, a.bandwidthUnit) : "",
    bandwidthUnit: (a?.bandwidthUnit ?? "Mbps") as BandwidthUnit,
    amount: a ? paiseToRupees(a.commitmentPaise) : "",
    gstMode: a?.priceIncludesGst ? "incl" : "plus",
    usageTerms: a?.usageTerms ?? "",
    gstPercent: a ? String(a.gstRateBp / 100) : "18",
    billingCycle: (a?.billingCycle ?? "monthly") as BillingCycle,
    termStart: a?.termStart ?? today(),
    termEnd: a?.termEnd ?? "",
    note: a?.note ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const amountPaise = f.chargeBasis === "usage" && f.amount.trim() === "" ? 0 : rupeesToPaise(f.amount);
  const mbps = toMbps(f.bandwidth, f.bandwidthUnit);
  const gstBp = Math.round(Number(f.gstPercent) * 100);
  const per = CYCLE_LABEL[f.billingCycle].per;
  const problem =
    f.supplierLegalName.trim() === ""
      ? "The supplier's legal name is required."
      : mbps === null
        ? f.bandwidthUnit === "Gbps"
          ? "Committed bandwidth is a number of Gbps above 0 (up to 3 decimals, e.g. 2.5)."
          : "Committed bandwidth is a whole number of Mbps above 0."
        : amountPaise === null
          ? "The charge is an amount in rupees."
          : f.chargeBasis !== "fixed" && f.usagePaidBy === ""
            ? "Say how the usage is paid: PINs or Funds."
          : f.chargeBasis !== "usage" && amountPaise === 0
            ? `A fixed agreement needs its charge ${per}.`
            : !Number.isFinite(gstBp) || gstBp < 0 || gstBp > 2800
              ? "GST is between 0 and 28%."
              : null;
  const save = useMutation({
    mutationFn: () => {
      const body: UpstreamAgreementInput = {
        supplierLegalName: f.supplierLegalName,
        supplierGstin: f.supplierGstin,
        supplierLicenseType: f.supplierLicenseType,
        supplierLicenseNumber: f.supplierLicenseNumber,
        agreementRef: f.agreementRef,
        chargeBasis: f.chargeBasis,
        ...(f.chargeBasis !== "fixed" && f.usagePaidBy !== "" ? { usagePaidBy: f.usagePaidBy } : {}),
        committedMbps: mbps ?? 0,
        bandwidthUnit: f.bandwidthUnit,
        commitmentPaise: amountPaise ?? 0,
        priceIncludesGst: f.gstMode === "incl",
        usageTerms: f.usageTerms,
        gstRateBp: gstBp,
        billingCycle: f.billingCycle,
        termStart: f.termStart,
        termEnd: f.termEnd,
        note: f.note,
      };
      return a ? updateAgreement(identity, ns, a.id, body) : createAgreement(identity, ns, body);
    },
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{a ? "Edit agreement" : "Add upstream agreement"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Supplier legal name" value={f.supplierLegalName} onChange={set("supplierLegalName")} required />
          <TextField label="Supplier GSTIN" value={f.supplierGstin} onChange={set("supplierGstin")} helperText="15 characters; its first two digits are the state." />
          <Stack direction="row" spacing={2}>
            <TextField select label="Supplier licence" value={f.supplierLicenseType} onChange={set("supplierLicenseType")} sx={{ minWidth: 140 }}>
              <MenuItem value="">Not recorded</MenuItem>
              {LICENCE_TYPES.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Licence number" value={f.supplierLicenseNumber} onChange={set("supplierLicenseNumber")} fullWidth />
          </Stack>
          <TextField label="Agreement or circuit reference" value={f.agreementRef} onChange={set("agreementRef")} />
          <TextField select label="How it is charged" value={f.chargeBasis} onChange={set("chargeBasis")}>
            {(Object.keys(CHARGE_BASIS_LABEL) as ChargeBasis[]).map((k) => (
              <MenuItem key={k} value={k}>
                {CHARGE_BASIS_LABEL[k]}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Committed bandwidth" value={f.bandwidth} onChange={set("bandwidth")} inputMode="decimal" required fullWidth />
            <TextField select label="Unit" value={f.bandwidthUnit} onChange={set("bandwidthUnit")} sx={{ minWidth: 110 }}>
              <MenuItem value="Mbps">Mbps</MenuItem>
              <MenuItem value="Gbps">Gbps</MenuItem>
            </TextField>
          </Stack>
          <TextField select label="Billed" value={f.billingCycle} onChange={set("billingCycle")}>
            {(Object.keys(CYCLE_LABEL) as BillingCycle[]).map((k) => (
              <MenuItem key={k} value={k}>
                {CYCLE_LABEL[k].name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label={`Charge ${per} (₹)`} value={f.amount} onChange={set("amount")} inputMode="decimal" fullWidth />
            <TextField select label="GST" value={f.gstMode} onChange={set("gstMode")} sx={{ minWidth: 150 }}>
              <MenuItem value="plus">+ GST (extra)</MenuItem>
              <MenuItem value="incl">Incl. GST</MenuItem>
            </TextField>
            <TextField select label="GST rate" value={f.gstPercent} onChange={set("gstPercent")} sx={{ minWidth: 110 }}>
              {GST_RATES.map((g) => (
                <MenuItem key={g} value={String(g)}>
                  {g}%
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {f.chargeBasis !== "fixed" && (
            <TextField select label="Usage paid by" value={f.usagePaidBy} onChange={set("usagePaidBy")} required>
              {(Object.keys(USAGE_PAID_BY_LABEL) as UsagePaidBy[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {USAGE_PAID_BY_LABEL[k]}
                </MenuItem>
              ))}
            </TextField>
          )}
          {f.chargeBasis !== "fixed" && <TextField label="Usage terms" value={f.usageTerms} onChange={set("usageTerms")} helperText="e.g. ₹400 per extra Mbps per month" />}
          <Stack direction="row" spacing={2}>
            <TextField label="Term starts" type="date" value={f.termStart} onChange={set("termStart")} InputLabelProps={{ shrink: true }} />
            <TextField label="Term ends (blank = open)" type="date" value={f.termEnd} onChange={set("termEnd")} InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField label="Note" value={f.note} onChange={set("note")} multiline minRows={2} />
          {problem && <Alert severity="info">{problem}</Alert>}
          {save.error && <Alert severity="error">{upstreamRefusal(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PAYMENT_LABEL: Record<UpstreamBill["paymentStatus"], string> = { unpaid: "Unpaid", part: "Part paid", paid: "Paid" };

function Bills({ ns, agreement, canWrite }: { ns: string; agreement: UpstreamAgreement; canWrite: boolean }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const key = ["upstream-bills", ns, agreement.id];
  const bills = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listBills(identity, ns, agreement.id, signal),
    retry: false,
  });
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<UpstreamBill | null>(null);
  const [voiding, setVoiding] = useState<UpstreamBill | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const live = (bills.data ?? []).filter((b) => b.status !== "void");
  const owed = live.reduce((s, b) => s + b.totalPaise - b.paidPaise, 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Supplier invoices</Typography>
        {live.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Outstanding {inr(owed)}
          </Typography>
        )}
        {canWrite && (
          <Button size="small" variant="outlined" onClick={() => setAdding(true)}>
            Record invoice
          </Button>
        )}
      </Stack>
      {bills.isLoading && <Typography color="text.secondary">Loading…</Typography>}
      {bills.error && <Alert severity="error">{upstreamRefusal(bills.error)}</Alert>}
      {bills.data && bills.data.length === 0 && <Typography color="text.secondary">No supplier invoice recorded yet.</Typography>}
      {bills.data && bills.data.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Taxable</TableCell>
                <TableCell align="right">GST</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Payment</TableCell>
                {canWrite && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.data.map((b) => (
                <TableRow key={b.id} sx={b.status === "void" ? { opacity: 0.55 } : undefined}>
                  <TableCell>
                    {b.supplierInvoiceNumber}
                    {b.status === "void" && (
                      <Typography variant="caption" display="block" color="error">
                        Void: {b.voidReason}
                      </Typography>
                    )}
                    {!b.itcClaimable && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Input GST not claimable
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {b.invoiceDate}
                    {b.periodFrom && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        for {b.periodFrom} – {b.periodTo ?? ""}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{inr(b.taxablePaise)}</TableCell>
                  <TableCell align="right">
                    {inr(b.igstPaise + b.cgstPaise + b.sgstPaise)}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {b.igstPaise > 0 ? "IGST" : b.cgstPaise > 0 ? "CGST + SGST" : "none"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{inr(b.totalPaise)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={b.paymentStatus === "paid" ? "success" : b.paymentStatus === "part" ? "warning" : "default"} label={PAYMENT_LABEL[b.paymentStatus]} />
                    {b.paidPaise > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {inr(b.paidPaise)}
                        {b.paymentRef ? ` · ${b.paymentRef}` : ""}
                      </Typography>
                    )}
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      {b.status !== "void" && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => setPaying(b)}>
                            Payment
                          </Button>
                          <Button size="small" color="error" onClick={() => setVoiding(b)}>
                            Void
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {adding && (
        <BillDialog
          ns={ns}
          agreement={agreement}
          onClose={() => setAdding(false)}
          onDone={() => {
            refresh();
            setAdding(false);
          }}
        />
      )}
      {paying && (
        <PaymentDialog
          ns={ns}
          bill={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            refresh();
            setPaying(null);
          }}
        />
      )}
      {voiding && (
        <ReasonDialog
          title={`Void invoice ${voiding.supplierInvoiceNumber}`}
          help="Only for an invoice recorded in error. It stays on record, struck through."
          action="Void invoice"
          run={(reason) => voidBill(identity, ns, voiding.id, reason)}
          onClose={() => setVoiding(null)}
          onDone={() => {
            refresh();
            setVoiding(null);
          }}
        />
      )}
    </Box>
  );
}

function BillDialog({ ns, agreement, onClose, onDone }: { ns: string; agreement: UpstreamAgreement; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState(agreement.commitmentPaise > 0 ? paiseToRupees(agreement.commitmentPaise) : "");
  const [incl, setIncl] = useState(agreement.priceIncludesGst);
  const [interState, setInterState] = useState(true);
  const [itc, setItc] = useState(true);
  const [note, setNote] = useState("");
  const amountPaise = rupeesToPaise(amount);
  const split =
    amountPaise === null
      ? null
      : incl
        ? fromInclusive(amountPaise, agreement.gstRateBp, interState)
        : { taxable: amountPaise, ...gstSplit(amountPaise, agreement.gstRateBp, interState) };
  const problem =
    number.trim() === "" || number.trim().length > 16
      ? "The supplier's invoice number, as printed (at most 16 characters)."
      : amountPaise === null
        ? "The amount is in rupees."
        : split === null
          ? "No taxable value gives exactly this total at this GST rate. Choose \"+ GST\" and type the taxable value printed on the invoice."
          : null;
  const save = useMutation({
    mutationFn: () =>
      recordBill(identity, ns, agreement.id, {
        supplierInvoiceNumber: number,
        invoiceDate: date,
        periodFrom: from,
        periodTo: to,
        taxablePaise: split?.taxable ?? 0,
        igstPaise: split?.igst ?? 0,
        cgstPaise: split?.cgst ?? 0,
        sgstPaise: split?.sgst ?? 0,
        totalPaise: split?.total ?? 0,
        itcClaimable: itc,
        note,
      }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Record a supplier invoice</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {agreement.supplierLegalName} · GST {agreement.gstRateBp / 100}%
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="Invoice number" value={number} onChange={(e) => setNumber(e.target.value)} required fullWidth />
            <TextField label="Invoice date" type="date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Period from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Period to" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label={incl ? "Invoice total incl. GST (₹)" : "Taxable value (₹)"} value={amount} onChange={(e) => setAmount(e.target.value)} required fullWidth />
            <TextField select label="GST" value={incl ? "incl" : "plus"} onChange={(e) => setIncl(e.target.value === "incl")} sx={{ minWidth: 150 }}>
              <MenuItem value="plus">+ GST (extra)</MenuItem>
              <MenuItem value="incl">Incl. GST</MenuItem>
            </TextField>
          </Stack>
          <RadioGroup row value={interState ? "igst" : "cgst"} onChange={(e) => setInterState(e.target.value === "igst")}>
            <FormControlLabel value="igst" control={<Radio size="small" />} label="IGST (supplier in another state)" />
            <FormControlLabel value="cgst" control={<Radio size="small" />} label="CGST + SGST (same state)" />
          </RadioGroup>
          {split && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2">
                Taxable {inr(split.taxable)} · {interState ? `IGST ${inr(split.igst)}` : `CGST ${inr(split.cgst)} + SGST ${inr(split.sgst)}`}
              </Typography>
              <Typography variant="subtitle2">Total {inr(split.total)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Check this matches the total printed on the supplier's invoice.
              </Typography>
            </Paper>
          )}
          <FormControlLabel control={<Checkbox checked={itc} onChange={(e) => setItc(e.target.checked)} />} label="Input GST is claimable (your CA decides)" />
          <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
          {problem && <Alert severity="info">{problem}</Alert>}
          {save.error && <Alert severity="error">{upstreamRefusal(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Record invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PaymentDialog({ ns, bill, onClose, onDone }: { ns: string; bill: UpstreamBill; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [paid, setPaid] = useState(paiseToRupees(bill.paidPaise > 0 ? bill.paidPaise : bill.totalPaise));
  const [paidAt, setPaidAt] = useState(bill.paidAt ? bill.paidAt.slice(0, 10) : today());
  const [ref, setRef] = useState(bill.paymentRef ?? "");
  const paidPaise = rupeesToPaise(paid);
  const problem = paidPaise === null ? "The amount paid is in rupees." : paidPaise > bill.totalPaise ? `At most the invoice total, ${inr(bill.totalPaise)}.` : null;
  const save = useMutation({
    mutationFn: () => setBillPayment(identity, ns, bill.id, { paidPaise: paidPaise ?? 0, paidAt: (paidPaise ?? 0) > 0 ? paidAt : "", paymentRef: ref }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Payment for {bill.supplierInvoiceNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Invoice total {inr(bill.totalPaise)}. Enter the total paid so far; 0 marks it unpaid.
          </Typography>
          <TextField label="Paid so far (₹)" value={paid} onChange={(e) => setPaid(e.target.value)} />
          <TextField label="Paid on" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="UTR / reference" value={ref} onChange={(e) => setRef(e.target.value)} />
          {problem && <Alert severity="info">{problem}</Alert>}
          {save.error && <Alert severity="error">{upstreamRefusal(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReasonDialog({
  title,
  help,
  action,
  run,
  onClose,
  onDone,
}: {
  title: string;
  help: string;
  action: string;
  run: (reason: string) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const go = useMutation({ mutationFn: () => run(reason.trim()), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {help}
          </Typography>
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} required autoFocus />
          {go.error && <Alert severity="error">{upstreamRefusal(go.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={reason.trim() === "" || go.isPending} onClick={() => go.mutate()}>
          {action}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
